import React, { useMemo, useEffect, useState } from 'react'
import { Box, BoxProps, Flex, Stack, Text, useDisclosure } from '../../../vanilla'
import { CheckIcon, PlusIcon } from '../../icons'
import { ModalNavigationProps } from '../../../types/ui'
import { Promotion, PromotionalBadgeText } from '../../../store/ProductStore'
import { ReactElement } from 'react-markdown/lib/react-markdown'
import { baseThemeContract } from '../../../vanilla/vars.css'
import { isMobile } from '../../../utils/utils'
import { nearPromoContainer } from './styles.css'
import { observer } from 'mobx-react-lite'
import { useBasketStore, useGlobalStore } from '../../../store/hooks/useStore'
import { useBookDeliveryPopover } from '../../../contexts'
import { usePromotion } from '../../../store/hooks/usePromotion'
import useNavigation from '../../../hooks/use-navigation'

interface MultiBuyPromoOfferMessageProps {
  promotion: Promotion
  productId?: string
  productIds?: string[]
  isPromoModal?: boolean
  leftAlign?: boolean
}

export const MultiBuyPromoOfferMessage = observer((props: MultiBuyPromoOfferMessageProps) => {
  const {promotion, productId, productIds, isPromoModal, leftAlign} = props
  const basketStore = useBasketStore()

  const promo = usePromotion(promotion.ID)
  // Grab the right information/promotion for the nearness messaging
  const matchedPromotion = basketStore.missedOffers?.promotions[promotion.ID]
  // Grab the current completed promotions
  const completedPromotion = basketStore.missedOffers?.completedPromos?.[promotion.ID]

  // Find the promotion group for the product
  const matchedPromotionGroup = useMemo(() => {
    if (!productId) {
      return undefined
    }

    return matchedPromotion?.groups?.find?.(group =>
      group.productIds?.includes(productId)
    )
  }, [matchedPromotion?.groups, productId])

  // Early return if no valid promo
  if (!promo || (!matchedPromotion && !completedPromotion)) {
    return null
  }

  // Total number of items in the basket relevant to the promotion
  const totalPromotionItemsQuantity =
    basketStore?.basket?.productItems
      ?.filter(({productId}) => productId && promo?.productIds?.includes(productId))
      ?.reduce((total, {quantity}) => (total += quantity || 0), 0) || 0

  // Items in the basket that are relevant to the promotion
  const getMatchingCartProducts = (() => {
    if (productIds) {
      const productIdArray = []
      for (const productId of productIds) {
        const productInBasket = basketStore?.basket?.productItems?.find(x => x.productId == productId)
        if (productInBasket) {
          productIdArray.push(productId)
        }
      }
      return productIdArray
    } else {
      return basketStore?.basket?.productItems?.find(
        (cartProduct) => cartProduct.productId === productId,
      )
    }
  })()

  // Boolean check on whether the selected item/items are in the basket (always true for promo modals)
  const isProductInBasket = isPromoModal
    ? true
    : Array.isArray(getMatchingCartProducts) && !!getMatchingCartProducts?.length || !Array.isArray(getMatchingCartProducts) && getMatchingCartProducts != null

  // Check to see if the product group is complete for the promotion
  const isProductGroupComplete = matchedPromotionGroup?.remainingQty === 0

  // Check to see if you've reached the promotion discount (ignore isProductInBasket for promo modals)
  const reachedPromotionDiscount = isPromoModal
    ? !!completedPromotion || matchedPromotion?.remainingQty === 0
    : isProductInBasket && (!!completedPromotion || matchedPromotion?.remainingQty === 0)

  // Creates the nearness message
  const getOfferMessage = () => {
    if (!totalPromotionItemsQuantity && (!matchedPromotion || !completedPromotion)) {
      return ''
    }

    if (completedPromotion || matchedPromotion?.remainingQty === 0) {
      return 'You got it'
    }

    if (matchedPromotionGroup?.remainingQty) {
      const remainingQty = (matchedPromotionGroup.remainingQty % matchedPromotionGroup.requiredQty) || matchedPromotionGroup.remainingQty
      const groupExceedFirstCycle = matchedPromotion?.exceededFirstCycle && matchedPromotionGroup.cartQty >= matchedPromotionGroup.requiredQty
      return `${remainingQty} more to get it ${groupExceedFirstCycle ? 'again' : ''}`
    }

    if (matchedPromotionGroup?.remainingQty === 0) {
      return 'Other products required'
    }

    return `${matchedPromotion?.remainingQty} more to get it ${matchedPromotion?.exceededFirstCycle ? 'again' : ''}`
  }

  return !matchedPromotion?.removeNearnessMessage && isProductInBasket ? (
    <Flex alignSelf={leftAlign ? 'flex-start' : 'flex-end'} gap="2px" marginY={leftAlign ? '0px' : '4px'}>
      {!isProductGroupComplete && (reachedPromotionDiscount
        ? <CheckIcon color="accent1" boxSize="12px" />
        : <PlusIcon color="promotionColor" boxSize="12px" />
      )}
      <Text
        variant="text1"
        lineHeight="none"
        color={reachedPromotionDiscount ? 'accent1' : 'promotionColor'}
      >
        {getOfferMessage()}
      </Text>
    </Flex>
  ) : null
})

interface NearPromoProps extends BoxProps<'div'> {
  promotion?: Promotion | null
  bundle?: PromotionalBadgeText | null
  disableInteraction?: boolean
  children?: ReactElement
  onNavigate?: (url: string) => void
  search?: boolean
  cartDrawer?: boolean
  modalNavigationProps?: ModalNavigationProps
  hitId?: string
}

export const NearPromo = observer((props: NearPromoProps) => {
  const {
    promotion,
    bundle,
    disableInteraction,
    children,
    onNavigate,
    search,
    cartDrawer,
    modalNavigationProps,
    hitId = '',
    ...rest
  } = props

  const promotionModal = useDisclosure(false)
  const {getCustomSitePreferenceById, isCustomSitePreferenceSet, customSitePreferences, setIsPromoModalOpen} = useGlobalStore()
  const [mixAndMatchText, setMixAndMatchText] = useState(false)
  const basketStore = useBasketStore()
  const {PopoverWrapperComponent, hideBookDeliveryPopover, showBookDeliveryPopover} = useBookDeliveryPopover()
  const navigate = typeof onNavigate === 'function' ? onNavigate : useNavigation()
  const isMobileFlag = isMobile()

  // Determine type of promotion
  const promo = usePromotion(promotion?.ID)
  const isMixAndMatch = promotion?.t === 'BuyXForTotal' && mixAndMatchText && promo?.productIds && promo?.productIds?.length > 1
  const isBuyXGetY = promotion?.t === 'BuyXGetY' && promotion?.buyXReq !== undefined && promotion?.getYQuantity !== undefined
  const isMealDealLanding = promo?.c_isMealDealLanding
  const mealDealUrl = promotion && isMealDealLanding ? encodeURI(`/meal-deals?promo=${promotion.ID}`) : ''

  const splitOfferMessage = () => {
    const [amount, price] = promotion?.msg.split('for') || []
    return (
      <Text variant="text5" textAlign="center" lineHeight="none" marginBottom="2px">
        {amount} <span style={{fontWeight: '400', fontSize: '12px'}}>for</span> {price}
      </Text>
    )
  }

  useEffect(() => {
    (async function () {
      if (!isCustomSitePreferenceSet('showMixandMatchTextOnMultibuyBadge')) {
        await getCustomSitePreferenceById('showMixandMatchTextOnMultibuyBadge')
      }
      setMixAndMatchText(customSitePreferences['showMixandMatchTextOnMultibuyBadge'] as boolean)
    }())
  })

  const handleOpenPromoModal = () => {
    promotionModal.onOpen()
  }

  const getNearPromoText = () => {
    // First check for promotion msg
    if (promotion && promotion.msg) {
      if (promotion.msg.includes('for')) {
        return splitOfferMessage()
      } else {
        return promotion.msg
      }
    }
    // If no promotion msg, check for bundle msg
    if (bundle?.msg) {
      return bundle.msg
    }
    // Fallback text
    return 'Offer'
  }

  return promo || bundle ? (
    <>
      <PopoverWrapperComponent
        hitId={`${hitId}${promo?.id}`}
        onClickOutside={() => hideBookDeliveryPopover()}
        containerStyle={{zIndex: baseThemeContract.zIndex.popover}}
      >
        <Box
          color="black"
          cursor={disableInteraction ? 'auto' : 'pointer'}
          display="flex"
          flexDirection="column"
          borderRadius="base"
          paddingY="4px"
          width="full"
          className={children ? '' : nearPromoContainer}
          // Only make it clickable if there's a promotion to show
          onClick={promo && !disableInteraction ?(e: React.MouseEvent | React.TouchEvent) => {
            if (isMealDealLanding) {
              navigate(mealDealUrl)
            } else {
              e.preventDefault()
              setIsPromoModalOpen(true, promotion ?? undefined)
            }
          } : undefined}
          {...rest}
        >
          {children ? (
            children
          ) : (
            <>
              <Stack
                gap='6px'
                direction='row'
                justifyItems='center'
                width="full"
                margin={bundle ? 'auto' : undefined}
                justifyContent={bundle ? 'center' : 'flex-start'}
              >
                <Text
                  display="flex"
                  alignItems="center"
                  align="center"
                  variant={search || cartDrawer ? "text2Bold" : "text5"}
                  fontSize="inherit"
                  fontWeight="inherit"
                  lineHeight="inherit"
                >
                  {isBuyXGetY
                    ? `Buy ${Number(promotion.buyXReq)} Get ${Number(promotion.getYQuantity)} FREE`
                    : getNearPromoText()
                  }
                </Text>
                {isMixAndMatch && mixAndMatchText && (
                  <Text
                    variant="unstyled"
                    style={{fontSize: '12px', fontWeight: '400'}}
                    display="flex"
                    alignItems="center"
                    lineHeight="none"
                  >
                    Mix & Match
                  </Text>
                )}
              </Stack>
              {promotion?.unitPriceMsg && (
                <Text
                  variant="unstyled"
                  style={{fontSize: '12px', fontWeight: '400'}}
                  lineHeight="inherit"
                  textAlign={'left'}
                  data-test-selector="promo-view-offer"
                >
                  {promotion?.unitPriceMsg}
                </Text>
              )}
            </>
          )}
        </Box>
      </PopoverWrapperComponent>
    </>
  ) : null
})
